import React, { useRef, useState } from "react";
import { NextPage } from "next";
import dynamic from "next/dynamic";
import { JsonLd } from "react-schemaorg";
import { Organization } from "schema-dts";

import { Brand } from "custom-types/Brand";
import useDomain from "hooks/useDomain";
import formatImgixUrl from "utils/image/formatImgixUrl";

import Button from "components/botanic/Button";
import { BrandHead } from "components/brands/BrandHead";
import BrandInfoSection from "components/brands/BrandInfoSection";
import BrandLogoHeading from "components/brands/BrandLogoHeading";
import ProductCatalogSection from "components/brands/ProductCatalogSection";
import ShopNearbySection from "components/brands/ShopNearbySection";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import ButtonPlaceholder from "components/ButtonPlaceholder";
import { SponsoredArticlesCarousel } from "components/products/SponsoredArticlesCarousel";

const MSOMap = dynamic(() => import("components/brands/MSOMap"));
const BrandPhotoCarousel = dynamic(
  () => import("components/brands/BrandPhotoCarousel")
);
const BrandFeaturedProducts = dynamic(
  () => import("components/brands/BrandFeaturedProducts")
);

type BrandPageProps = {
  brand: Brand;
  initialCategory: string;
};

const BrandPage: NextPage<BrandPageProps> = ({ brand, initialCategory }) => {
  // The null value represent that products are being loaded.
  const [hasProducts, setHasProducts] = useState<boolean | null>(null);

  const productCatalogRef = useRef<HTMLDivElement>(null);

  const domain = useDomain();

  const navigateToCatalog = () => {
    productCatalogRef.current?.scrollIntoView({ behavior: "smooth" });
    productCatalogRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <BrandHead brand={brand}>
        <JsonLd<Organization>
          item={{
            "@context": "https://schema.org",
            "@type": "Organization",
            description: brand.description,
            // TODO id's seem to not be permitted on Organization schemas
            // id: `${domain}/brands/${brand.name}`,
            logo: brand.logo
              ? [`${formatImgixUrl(brand.logo)}?fit=clip&w=500`]
              : "",
            name: brand.name,
            url: `${domain}/brands/${brand.name}`,
          }}
        />
      </BrandHead>
      <div className="bg-white flex flex-col gap-xl">
        <div>
          <Breadcrumbs>
            <Breadcrumb href="/brands">Brand</Breadcrumb>
            <Breadcrumb href={`/brands/${brand.slug}`} currentLocation>
              {brand.name}
            </Breadcrumb>
          </Breadcrumbs>

          <div className="container bpp-header">
            <div className="bpp-header-logo mb-lg md:mb-none">
              <BrandLogoHeading
                name={brand.name}
                claimed={brand.claimed}
                tagline={brand.tagline}
                logo={brand.logo}
              />
            </div>

            {brand.claimed && brand.photos.length > 0 && (
              <div className="bpp-header-photo mt-xl">
                <BrandPhotoCarousel photos={brand.photos} />
              </div>
            )}
            <div
              className="bpp-header-button mt-xl md:mt-none"
              style={{ minHeight: 88 }}
            >
              <div className="center block">
                {hasProducts === null ? (
                  <ButtonPlaceholder height="48" className="shop-button" />
                ) : (
                  hasProducts && (
                    <Button
                      className="shop-button"
                      onClick={navigateToCatalog}
                      data-testid="catalog-jump-button"
                    >
                      shop our products
                    </Button>
                  )
                )}
                {brand.informationWebsite && (
                  <a
                    className="cursor-pointer text-green underline block text-center p-sm"
                    href={brand.informationWebsite}
                    rel="nofollow noopener"
                    data-testid="brand-egress-link"
                  >
                    {`visit ${brand.informationWebsite.replace(
                      /\s*https?:\/\/(www\.)?([^?/]+).*/i,
                      "$2"
                    )}`}
                  </a>
                )}
              </div>
            </div>
            <style jsx>{`
              /* re-order layout on larger screens */
              @media only screen and (min-width: 768px) {
                .bpp-header {
                  display: grid;
                  column-gap: 1rem;
                  grid-template-areas:
                    "logo   logo   button"
                    "photo  photo  photo";
                }
                .bpp-header-logo {
                  grid-area: logo;
                }
                .bpp-header-photo {
                  grid-area: photo;
                }
                .bpp-header-button {
                  grid-area: button;
                  display: flex;
                  justify-content: flex-end;
                  align-items: center;
                }
                .bpp-header-link {
                  display: flex;
                  justify-content: center;
                  margin: 0;
                }
              }
            `}</style>
            <style jsx global>{`
              .shop-button {
                width: 100%;
              }
              @media only screen and (min-width: 768px) {
                .shop-button {
                  width: 322px;
                }
              }
            `}</style>
          </div>
        </div>

        <div className="bg-leafly-white">
          <div className="container">
            <ShopNearbySection brand={brand} />
          </div>
        </div>

        {brand.claimed && <BrandInfoSection brand={brand} />}

        {brand.claimed && brand.organizationId && (
          <MSOMap organizationId={brand.organizationId} />
        )}

        {brand.claimed && brand.featuredProducts.length ? (
          <div className="border-t border-light-grey pt-xl">
            <BrandFeaturedProducts brand={brand} />
          </div>
        ) : (
          <></>
        )}

        {!brand.claimed && (
          <div className="container" data-testid="unclaimed-brand-section">
            <h2 className="mb-sm">Where’s the brand?</h2>
            <p className="mb-md">
              This brand hasn’t claimed their profile yet. Is this your
              business? Claim your brand to make shopping your products an easy
              and seamless experience on Leafly.
            </p>
            <Button
              className="lowercase mt-md"
              href={`https://success.leafly.com/claim-brand?brand_name=${encodeURIComponent(
                brand.name
              )}`}
              rel="nofollow noopener"
              target="_blank"
            >
              Claim your brand
            </Button>
          </div>
        )}

        <div
          className="border-light-grey border-t"
          ref={productCatalogRef}
          tabIndex={-1}
          style={{ scrollMarginTop: "var(--header-height)" }}
        >
          <ProductCatalogSection
            className="container py-xl"
            brand={brand}
            onProductsFetch={({ products }) => {
              setHasProducts(products.length > 0);
            }}
            initialCategory={initialCategory}
          />
        </div>
        <SponsoredArticlesCarousel
          sponsorId={brand.id}
          sponsorType="brand"
          className="container border-light-grey border-t py-xl"
        />
      </div>
    </>
  );
};

export default BrandPage;
